import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const qc = useQueryClient();
  // Honour `?next=/some/path` so that pages like the public quest
  // board can send anonymous users to login and have them return to
  // the page they came from. The default landing is /player. Only
  // allow same-origin paths (start with `/`, no scheme or `//`).
  const nextParam = searchParams.get('next') ?? '';
  const next = nextParam.startsWith('/') && !nextParam.startsWith('//') ? nextParam : '/player';

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      // M5.9+ fix: login now returns the user object in
      // the response (see apps/api/src/api/auth.routes.ts).
      // We populate the me cache directly from the response
      // with setQueryData, which is synchronous — no
      // refetch needed. The old flow was:
      //   await login;
      //   await invalidateQueries(['me']);  // fires refetch
      //   navigate('/player');
      // That had a Set-Cookie timing race: api() resolves on
      // the microtask queue, but the browser commits
      // Set-Cookie to the jar on the next macrotask. The
      // me refetch fired with the stale cookie, got 401,
      // put me into error state, and the auth gate
      // redirected back to /login on first attempt. The
      // user saw: 'correct password, returns to login, no
      // error, second attempt works.'
      //
      // With setQueryData, the cache is populated
      // synchronously from the login response. The next
      // fetch (if any) sends the now-committed cookie; no
      // race possible. The destination route's
      // useQuery(['me']) finds the populated cache and
      // renders immediately.
      const res = await api<{ ok: true; user: unknown }>(
        '/api/auth/login',
        { method: 'POST', body: { email, password } },
      );
      // Clear any leftover cache (e.g. from a session that
      // expired without an explicit logout) so a different
      // user never sees the previous user's cached data.
      qc.clear();
      qc.setQueryData(['me'], { user: res.user });
      navigate(next, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('Email or password is incorrect.');
      } else {
        setError('Sign-in failed. Try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-full place-items-center bg-bg p-4">
      <form onSubmit={onSubmit} className="card w-full max-w-sm space-y-4">
        <div className="flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-md text-bg" style={{ backgroundImage: 'linear-gradient(135deg, var(--fantasy-gold) 0%, #2a1f0a 100%)' }}>M</span>
          <h1 className="text-lg font-semibold">Sign in to Mirabili</h1>
        </div>
        <div className="space-y-1">
          <label className="label" htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <label className="label" htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            className="input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {error ? <p className="text-sm text-accent-danger" role="alert">{error}</p> : null}
        <button type="submit" className="btn-primary w-full" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
